import * as React from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document, INLINES, MARKS } from '@contentful/rich-text-types';
import { FilloutStandardEmbed } from '@fillout/react';
import { RichTextFieldsFragment } from 'types/generated';
import { ImageWithFocalPoint } from '../ImageWithFocalPoint';
import { formatLink } from '@/utils/utils';
import clsx from 'clsx';
import { Heading, Paragraph, Subheading } from 'components/src/typography';
import Link from 'next/link';
import UnorderedList from './UnorderedList';
import VideoPlayer from '../VideoPlayer';
import dynamic from 'next/dynamic';
import ListItem from '@/components/RichTextSection/ListItem';

const VideoJSPlayer = dynamic(() => import('@/components/VideoJS/VideoPlayer'), { ssr: false });

type Props = {
  data: RichTextFieldsFragment;
  textAlignment?: string | null | undefined;
  isFilloutStandardEmbed?: boolean;
  paragraphVariant?: 'small' | 'medium' | 'large' | undefined;
};

type BoldProps = {
  children: React.ReactNode;
};

const getEntry = (data: RichTextFieldsFragment, id: string) => {
  if (!data?.links.entries.block) {
    return;
  }

  return data.links.entries.block.find((x) => x?.sys.id === id);
};

const RichText = ({
  data,
  textAlignment = 'right-text',
  isFilloutStandardEmbed = false,
  paragraphVariant = 'medium',
}: Props) => {
  const allStyle = `text-left col-span-10 text-black-50 break-word overflow-hidden ${clsx({
    'text-center': textAlignment === 'Center Align',
  })}`;
  const content = data?.json as Document;
  const imageIds = content?.content.reduce<string[]>((acc, x, index) => {
    if (x.nodeType === 'embedded-entry-block') {
      if (content.content[index + 1]?.nodeType === 'embedded-entry-block') {
        acc.push(content.content[index + 1].data.target.sys.id, x.data.target.sys.id);
      }
    }
    return acc;
  }, []);

  const Bold = ({ children }: BoldProps) => <span className="font-bold">{children}</span>;
  return (
    <div className="rich-text-container">
      {documentToReactComponents(data.json as Document, {
        renderMark: {
          [MARKS.BOLD]: (text) => <Bold>{text}</Bold>,
        },
        renderNode: {
          [BLOCKS.HEADING_6]: (_node, children) => <Subheading classes={`${allStyle}`}>{children}</Subheading>,
          [BLOCKS.HEADING_5]: (_node, children) => (
            <Heading classes={allStyle} variant="xs">
              {children}
            </Heading>
          ),
          [BLOCKS.HEADING_4]: (_node, children) => (
            <Heading classes={allStyle} variant="small">
              {children}
            </Heading>
          ),
          [BLOCKS.HEADING_3]: (_node, children) => (
            <Heading classes={allStyle} variant="medium">
              {children}
            </Heading>
          ),
          [BLOCKS.HEADING_2]: (_node, children) => (
            <Heading classes={allStyle} variant="large">
              {children}
            </Heading>
          ),
          [BLOCKS.HEADING_1]: (_node, children) => (
            <Heading classes={allStyle} variant="xl">
              {children}
            </Heading>
          ),
          [BLOCKS.PARAGRAPH]: (_node, children) => {
            if (isFilloutStandardEmbed) {
              return <FilloutStandardEmbed filloutId={`${children}`} dynamicResize={true} />;
            }
            return (
              <Paragraph variant={paragraphVariant} classes={allStyle}>
                {children}
              </Paragraph>
            );
          },
          [BLOCKS.QUOTE]: (_node, children) => {
            return <div className={allStyle}>{children}</div>;
          },
          [INLINES.HYPERLINK]: (node, children) => {
            return (
              <Link
                href={formatLink(node.data.uri)}
                className={`${allStyle} hover:decoration-red-100" text-red-100 hover:underline`}
              >
                {children}
              </Link>
            );
          },
          [BLOCKS.HR]: (_node, children) => {
            return <div>{children}</div>;
          },
          [BLOCKS.UL_LIST]: (_node, children) => {
            return <UnorderedList classes={'col-span-10 list-disc text-red-100'}>{children}</UnorderedList>;
          },
          [BLOCKS.LIST_ITEM]: (_node, children) => {
            return <ListItem>{children}</ListItem>;
          },
          [BLOCKS.EMBEDDED_ENTRY]: (node) => {
            const entry = getEntry(data, node.data.target.sys.id);
            if (entry?.__typename === 'ImageWithFocalPoint') {
              const isImageSideBySide = imageIds.includes(node.data.target.sys.id);
              if (!entry.keepImageRatio) {
                return (
                  <div
                    key={entry.sys.id}
                    className={`relative col-span-10 h-[360px] lg:h-[496px] ${clsx({
                      'md:col-span-5': isImageSideBySide,
                    })}`}
                  >
                    <ImageWithFocalPoint image={entry} sectionId={entry.id} className="rounded-28" />
                  </div>
                );
              } else {
                return (
                  <div
                    key={entry.sys.id}
                    style={{
                      height: entry.image?.height ?? 'auto',
                      width: entry.image?.width ?? 'auto',
                      maxWidth: entry.image?.width ?? 'auto',
                      maxHeight: entry.image?.height ?? 'auto',
                    }}
                    className={`relative col-span-10 ${clsx({
                      'md:col-span-5': isImageSideBySide,
                    })}`}
                  >
                    <ImageWithFocalPoint image={entry} sectionId={entry.id} className="rounded-28" />
                  </div>
                );
              }
            } else if (entry?.__typename === 'Video') {
              return (
                <div key={entry.sys.id} className={`relative col-span-10 h-[360px] lg:h-[496px]`}>
                  <VideoPlayer
                    videoId={entry.youtubeId}
                    autoPlay={entry.autoplay}
                    muted={entry.muted}
                    loop={entry.loop}
                  />
                </div>
              );
            } else if (entry?.__typename === 'VideoJs') {
              return (
                <div key={entry.sys.id} className={`relative col-span-10 h-full w-full`}>
                  <VideoJSPlayer
                    options={{
                      sources: [
                        {
                          src: entry.mediaFileUrl,
                          type: 'application/x-mpegURL',
                        },
                      ],
                      ...entry,
                    }}
                  />
                </div>
              );
            }
            return null;
          },
        },
      })}
    </div>
  );
};
export default RichText;
